import { showNotice } from "../lib/confirmDialog";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import type {
  FriendConnectionOut,
  FriendListResponse,
  FriendSearchResult,
  FriendlyMatchOut,
} from "../lib/api";
import { api } from "../lib/api";
import { scoreTextFromWinner } from "../lib/scoreFromWinner";
import Icon from "./icons";

/** Amistoso: ¿ganó el jugador 1? null sin ganador. El marcador del amistoso
 * también se guarda desde el jugador 1 (Eduardo 2026-10-06). */
function friendlyWinnerIsPlayer1(match: FriendlyMatchOut): boolean | null {
  if (match.winner_profile_id == null) return null;
  if (match.winner_profile_id === match.player1.player_profile_id) return true;
  if (match.winner_profile_id === match.player2.player_profile_id) return false;
  return null;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function formatDateShort(dateStr: string | null): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y?.slice(2)}`;
}

interface AmigosPanelProps {
  myProfileId?: number;
  opponentProfileId?: number | null;
  isBye?: boolean;
}

type Modal =
  | { type: "search" }
  | { type: "createMatch"; friend: FriendConnectionOut }
  | { type: "reportResult"; match: FriendlyMatchOut }
  | null;

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function statusBadge(status: FriendSearchResult["friend_status"]): string {
  switch (status) {
    case "accepted":
      return "✅ Amigo";
    case "pending_sent":
      return "⏳ Enviada";
    case "pending_received":
      return "📩 Te envió solicitud";
    default:
      return "";
  }
}

function matchResultLabel(result: FriendlyMatchOut["result"]): string {
  switch (result) {
    case "win":
      return "🏆 Ganaste";
    case "loss":
      return "Perdiste";
    default:
      return "Pendiente";
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "🎾";
}

function visiblePlayerId(playerId: string | null | undefined): string | null {
  if (!playerId) return null;
  return playerId.trim().toLowerCase().startsWith("legacy") ? null : playerId;
}

function h2hLabel(friend: FriendConnectionOut): string {
  const summary = friend.h2h_summary;
  const total = (summary?.wins ?? 0) + (summary?.losses ?? 0) + (summary?.draws ?? 0);
  if (!total) return "Vs ti: sin partidos";
  return `Vs ti: ${summary.wins}–${summary.losses}`;
}

function friendMeta(friend: FriendConnectionOut): string[] {
  return [friend.friend.primary_category, friend.friend.city, friend.friend.club_name].filter(
    (value): value is string => Boolean(value),
  );
}

const FRIENDLY_SCORE_COLUMNS = ["Set 1", "Set 2", "Set 3", "Set 4", "Set 5", "TB"] as const;
type FriendlyScoreColumn = (typeof FRIENDLY_SCORE_COLUMNS)[number];
type FriendlyScoreDraft = Record<"p1" | "p2", Record<FriendlyScoreColumn, string>>;

function emptyScoreDraft(): FriendlyScoreDraft {
  return {
    p1: { "Set 1": "", "Set 2": "", "Set 3": "", "Set 4": "", "Set 5": "", TB: "" },
    p2: { "Set 1": "", "Set 2": "", "Set 3": "", "Set 4": "", "Set 5": "", TB: "" },
  };
}

function friendlyScoreTextFromDraft(draft: FriendlyScoreDraft): string {
  return FRIENDLY_SCORE_COLUMNS.flatMap((column) => {
    const p1 = draft.p1[column].trim();
    const p2 = draft.p2[column].trim();
    if (!p1 && !p2) return [];
    const score = `${p1 || "0"}-${p2 || "0"}`;
    return column === "TB" ? [`TB ${score}`] : [score];
  }).join(", " );
}

function friendlySetsFromDraft(draft: FriendlyScoreDraft): { p1: number; p2: number } {
  return FRIENDLY_SCORE_COLUMNS.reduce(
    (totals, column) => {
      const p1 = Number(draft.p1[column]);
      const p2 = Number(draft.p2[column]);
      if (!Number.isFinite(p1) || !Number.isFinite(p2) || (p1 === 0 && p2 === 0)) return totals;
      if (p1 > p2) totals.p1 += 1;
      if (p2 > p1) totals.p2 += 1;
      return totals;
    },
    { p1: 0, p2: 0 },
  );
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AmigosPanel({
  myProfileId,
  opponentProfileId: _opponentProfileId,
}: AmigosPanelProps) {
  const { token } = useAuth();
  const { askConfirm } = useAbortableConfirm([myProfileId]);

  const [expanded, setExpanded] = useState(false);
  const [friends, setFriends] = useState<FriendListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<FriendSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Create match state
  const [matchDate, setMatchDate] = useState("");
  const [matchNotes, setMatchNotes] = useState("");
  const [matchSubmitting, setMatchSubmitting] = useState(false);

  // Report result state
  const [reportWinner, setReportWinner] = useState<number | null>(null);
  const [reportScoreDraft, setReportScoreDraft] = useState<FriendlyScoreDraft>(() => emptyScoreDraft());
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // Friendly matches list
  const [fmList, setFmList] = useState<FriendlyMatchOut[]>([]);
  const [fmLoading, setFmLoading] = useState(false);
  const [fmFilterLabel, setFmFilterLabel] = useState<string | null>(null);

  /* ── Load friends on expand ─────────────────── */
  const loadFriends = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.player.friends.list(token, myProfileId);
      if (resp.ok) setFriends(resp.data);
    } catch {
      setError("No se pudo cargar la lista de amigos");
    } finally {
      setLoading(false);
    }
  }, [myProfileId, token]);

  const loadFriendlyMatches = useCallback(async (friend?: FriendConnectionOut) => {
    if (!token) return;
    setFmLoading(true);
    try {
      const resp = friend
        ? await api.player.friendlyMatches.listWith(token, friend.friend.player_profile_id, myProfileId)
        : await api.player.friendlyMatches.list(token, myProfileId);
      if (resp.ok) {
        setFmList(resp.data);
        setFmFilterLabel(friend?.friend.display_name ?? null);
      }
    } finally {
      setFmLoading(false);
    }
  }, [myProfileId, token]);

  useEffect(() => {
    if (expanded) {
      loadFriends();
      loadFriendlyMatches();
    }
  }, [expanded, loadFriends, loadFriendlyMatches]);

  /* ── Search ─────────────────────────────── */
  const doSearch = useCallback(async () => {
    if (!token || searchQuery.trim().length < 2) return;
    setHasSearched(true);
    setSearchLoading(true);
    setSearchError(null);
    try {
      const resp = await api.player.friends.search(token, searchQuery.trim(), myProfileId);
      if (resp.ok) setSearchResults(resp.data);
    } catch {
      setSearchError("Error al buscar jugadores");
    } finally {
      setSearchLoading(false);
    }
  }, [myProfileId, token, searchQuery]);

  const sendRequest = useCallback(
    async (profileId: number) => {
      if (!token) return;
      try {
        await api.player.friends.sendRequest(token, profileId, myProfileId);
        doSearch();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Error";
        void showNotice(msg);
      }
    },
    [myProfileId, token, doSearch],
  );

  const respondRequest = useCallback(
    async (friendId: number, action: "accept" | "reject") => {
      if (!token) return;
      try {
        await api.player.friends.respond(token, friendId, action, myProfileId);
        loadFriends();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Error";
        void showNotice(msg);
      }
    },
    [myProfileId, token, loadFriends],
  );

  const removeFriend = useCallback(
    async (profileId: number) => {
      if (!token) return;
      if (!(await askConfirm("¿Eliminar a este amigo?", { acceptLabel: "Sí, eliminar amigo", tone: "danger" }))) return;
      try {
        await api.player.friends.remove(token, profileId, myProfileId);
        loadFriends();
        loadFriendlyMatches();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Error";
        void showNotice(msg);
      }
    },
    [myProfileId, token, loadFriends, loadFriendlyMatches],
  );

  /* ── Create friendly match ──────────────── */
  const createMatch = useCallback(async () => {
    if (!token || modal?.type !== "createMatch") return;
    setMatchSubmitting(true);
    try {
      await api.player.friendlyMatches.create(token, {
        friend_profile_id: modal.friend.friend.player_profile_id,
        played_at: matchDate || undefined,
        notes: matchNotes || undefined,
      }, myProfileId);
      setModal(null);
      setMatchDate("");
      setMatchNotes("");
      loadFriendlyMatches();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error";
      void showNotice(msg);
    } finally {
      setMatchSubmitting(false);
    }
  }, [myProfileId, token, modal, matchDate, matchNotes, loadFriendlyMatches]);

  /* ── Report result ──────────────────────── */
  const reportResult = useCallback(async () => {
    if (!token || modal?.type !== "reportResult" || reportWinner === null) return;
    const sets = friendlySetsFromDraft(reportScoreDraft);
    const scoreText = friendlyScoreTextFromDraft(reportScoreDraft);
    setReportSubmitting(true);
    try {
      await api.player.friendlyMatches.reportResult(token, modal.match.id, {
        winner_profile_id: reportWinner,
        score_p1_sets: sets.p1 || undefined,
        score_p2_sets: sets.p2 || undefined,
        score_text: scoreText || undefined,
      }, myProfileId);
      setModal(null);
      setReportWinner(null);
      setReportScoreDraft(emptyScoreDraft());
      loadFriendlyMatches();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error";
      void showNotice(msg);
    } finally {
      setReportSubmitting(false);
    }
  }, [myProfileId, token, modal, reportWinner, reportScoreDraft, loadFriendlyMatches]);

  const updateReportScore = useCallback(
    (player: "p1" | "p2", column: FriendlyScoreColumn, value: string) => {
      setReportScoreDraft((current) => ({
        ...current,
        [player]: {
          ...current[player],
          [column]: value,
        },
      }));
    },
    [],
  );

  /* ── Render ─────────────────────────────── */

  const pendingCount = (friends?.pending_received?.length ?? 0) + (friends?.pending_sent?.length ?? 0);

  return (
    <>
      <article className="amigos-panel">
        {/* Renglón compacto plegable (Eduardo 2026-10-05, maqueta aprobada):
            insignia de amigos, contador de solicitudes y flecha. */}
        <button
          type="button"
          className="amigos-panel__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((o) => !o)}
        >
          <span className="amigos-panel__icon" aria-hidden="true">
            <Icon name="users" />
          </span>
          <span className="amigos-panel__title">Amigos</span>
          {pendingCount > 0 && (
            <span className="amigos-panel__badge">
              {pendingCount}
              <span className="sr-only"> {pendingCount === 1 ? "solicitud pendiente" : "solicitudes pendientes"}</span>
            </span>
          )}
          <Icon name="chevron-down" className={`amigos-panel__arrow${expanded ? " is-open" : ""}`} />
        </button>

        {expanded && (
          <div className="amigos-panel__body">
            {loading ? (
              <p className="amigos-panel__loading">Cargando amigos...</p>
            ) : error ? (
              <p className="amigos-panel__error">{error}</p>
            ) : (
              <>
                {/* ── Actions ─────────────────────────── */}
                <div className="amigos-panel__actions">
                  <button
                    type="button"
                    className="amigos-panel__action-btn"
                    onClick={() => {
                      setSearchQuery("");
                      setSearchResults([]);
                      setHasSearched(false);
                      setModal({ type: "search" });
                    }}
                  >
                    🔍 Buscar amigos
                  </button>
                </div>

                {/* ── Pending received ────────────────── */}
                {friends && friends.pending_received.length > 0 && (
                  <div className="amigos-panel__section">
                    <h4 className="amigos-panel__section-title">
                      📩 Solicitudes recibidas ({friends.pending_received.length})
                    </h4>
                    {friends.pending_received.map((req) => (
                      <div key={req.id} className="amigos-panel__friend-row">
                        <span className="amigos-panel__friend-name">
                          {req.friend.display_name}
                        </span>
                        <div className="amigos-panel__friend-actions">
                          <button
                            type="button"
                            className="amigos-panel__mini-btn amigos-panel__mini-btn--accept"
                            onClick={() => respondRequest(req.id, "accept")}
                          >
                            ✅ Aceptar
                          </button>
                          <button
                            type="button"
                            className="amigos-panel__mini-btn amigos-panel__mini-btn--reject"
                            onClick={() => respondRequest(req.id, "reject")}
                          >
                            ✕ Rechazar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Friends list ────────────────────── */}
                <div className="amigos-panel__section">
                  <h4 className="amigos-panel__section-title">
                    {friends ? `${friends.friends.length} amigo${friends.friends.length !== 1 ? "s" : ""}` : "Amigos"}
                  </h4>
                  {friends?.friends.length === 0 ? (
                    <p className="amigos-panel__empty">
                      Aún no tienes amigos en Torneos123. Busca jugadores para conectar: puedes buscar por correo, ID de usuario o nombre.
                    </p>
                  ) : (
                    friends?.friends.map((f) => {
                      const meta = friendMeta(f);
                      return (
                        <div key={f.id} className="amigos-panel__friend-card">
                          <div className="amigos-panel__avatar" aria-hidden="true">
                            {initials(f.friend.display_name)}
                          </div>
                          <div className="amigos-panel__friend-main">
                            <div className="amigos-panel__friend-title-row">
                              <span className="amigos-panel__friend-name">
                                {f.friend.display_name}
                              </span>
                              {visiblePlayerId(f.friend.unique_player_id) && (
                                <span className="amigos-panel__friend-id">
                                  #{visiblePlayerId(f.friend.unique_player_id)}
                                </span>
                              )}
                            </div>
                            {meta.length > 0 && (
                              <div className="amigos-panel__friend-meta">
                                {meta.map((item) => (
                                  <span key={item}>{item}</span>
                                ))}
                              </div>
                            )}
                            <div className="amigos-panel__friend-chips">
                              <span className="amigos-panel__chip amigos-panel__chip--h2h">
                                {h2hLabel(f)}
                              </span>
                              {f.h2h_summary.last_score ? (
                                <span className="amigos-panel__chip">
                                  Último: {f.h2h_summary.last_score}
                                  {f.h2h_summary.last_date ? ` · ${formatDateShort(f.h2h_summary.last_date)}` : ""}
                                </span>
                              ) : (
                                <span className="amigos-panel__chip">Sin partidos</span>
                              )}
                              {f.friendly_matches_count > 0 && (
                                <span className="amigos-panel__chip">{f.friendly_matches_count} amistoso{f.friendly_matches_count !== 1 ? "s" : ""}</span>
                              )}
                            </div>
                          </div>
                          <div className="amigos-panel__friend-actions amigos-panel__friend-actions--stacked">
                            <button
                              type="button"
                              className="amigos-panel__mini-btn"
                              onClick={() => loadFriendlyMatches(f)}
                            >
                              Ver partidos
                            </button>
                            <button
                              type="button"
                              className="amigos-panel__mini-btn amigos-panel__mini-btn--challenge"
                              onClick={() => setModal({ type: "createMatch", friend: f })}
                            >
                              🎾 Retar
                            </button>
                            <button
                              type="button"
                              className="amigos-panel__mini-btn amigos-panel__mini-btn--danger amigos-panel__mini-btn--icon"
                              onClick={() => removeFriend(f.friend.player_profile_id)}
                              aria-label={`Quitar a ${f.friend.display_name}`}
                              title="Quitar amigo"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* ── Pending sent ────────────────────── */}
                {friends && friends.pending_sent.length > 0 && (
                  <div className="amigos-panel__section">
                    <h4 className="amigos-panel__section-title">
                      ⏳ Solicitudes enviadas ({friends.pending_sent.length})
                    </h4>
                    {friends.pending_sent.map((req) => (
                      <div key={req.id} className="amigos-panel__friend-row amigos-panel__friend-row--pending">
                        <span>{req.friend.display_name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Friendly matches history ────────── */}
                <div className="amigos-panel__section">
                  <div className="amigos-panel__section-heading-row">
                    <h4 className="amigos-panel__section-title">
                      🎾 Partidos amistosos{fmFilterLabel ? ` vs ${fmFilterLabel}` : ""}
                    </h4>
                    {fmFilterLabel && (
                      <button
                        type="button"
                        className="amigos-panel__link-btn"
                        onClick={() => loadFriendlyMatches()}
                      >
                        Ver todos
                      </button>
                    )}
                  </div>
                  {fmLoading ? (
                    <p className="amigos-panel__loading">Cargando...</p>
                  ) : fmList.length === 0 ? (
                    <div className="amigos-panel__empty-card">
                      <strong>No hay partidos amistosos aún.</strong>
                      <span>
                        Elige un amigo, captura fecha y score. Estos partidos cuentan en tu historial amistoso, separado de torneos.
                      </span>
                      <button
                        type="button"
                        className="amigos-panel__action-btn amigos-panel__action-btn--primary"
                        onClick={() => {
                          const firstFriend = friends?.friends[0];
                          if (firstFriend) setModal({ type: "createMatch", friend: firstFriend });
                          else setModal({ type: "search" });
                        }}
                      >
                        Crear partido amistoso
                      </button>
                    </div>
                  ) : (
                    <div className="amigos-panel__matches-list">
                      {fmList.map((m) => (
                        <div key={m.id} className="amigos-panel__match-card">
                          <div className="amigos-panel__match-header">
                            <span className={`amigos-panel__match-result amigos-panel__match-result--${m.result}`}>
                              {matchResultLabel(m.result)}
                            </span>
                            <span className="amigos-panel__match-date">
                              {m.played_at ? formatDateShort(m.played_at) : "Sin fecha"}
                            </span>
                          </div>
                          <div className="amigos-panel__match-players">
                            {/* Eduardo 2026-10-06: ganador en negritas y marcador desde quien ganó. */}
                            {friendlyWinnerIsPlayer1(m) === true ? <strong>{m.player1.display_name}</strong> : m.player1.display_name}
                            {" vs "}
                            {friendlyWinnerIsPlayer1(m) === false ? <strong>{m.player2.display_name}</strong> : m.player2.display_name}
                          </div>
                          {m.score_text && (
                            <div className="amigos-panel__match-score">
                              {scoreTextFromWinner(m.score_text, friendlyWinnerIsPlayer1(m))}
                            </div>
                          )}
                          {m.result === "unknown" && (
                            <button
                              type="button"
                              className="amigos-panel__mini-btn"
                              onClick={() => {
                                setReportWinner(m.winner_profile_id);
                                setReportScoreDraft(emptyScoreDraft());
                                setModal({ type: "reportResult", match: m });
                              }}
                            >
                              ✏️ Reportar
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </article>

      {/* ── Search Modal ────────────────────────── */}
      {modal?.type === "search" && (
        <div className="amigos-modal__overlay" onClick={() => setModal(null)}>
          <div className="amigos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="amigos-modal__header">
              <h3>🔍 Buscar jugadores</h3>
              <button type="button" className="amigos-modal__close" onClick={() => setModal(null)}>
                ✕
              </button>
            </div>
            <div className="amigos-modal__body">
              <div className="amigos-modal__search-row">
                <input
                  type="text"
                  className="amigos-modal__input"
                  placeholder="Nombre, correo o ID del jugador…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  autoFocus
                />
                <button
                  type="button"
                  className="amigos-panel__action-btn"
                  onClick={doSearch}
                  disabled={searchLoading || searchQuery.trim().length < 2}
                >
                  Buscar
                </button>
              </div>
              {searchError && <p className="amigos-panel__error">{searchError}</p>}
              <div className="amigos-modal__results">
                {searchLoading ? (
                  <p className="amigos-panel__loading">Buscando...</p>
                ) : (
                  searchResults.map((p) => (
                    <div key={p.player_profile_id} className="amigos-panel__friend-row">
                      <span>
                        {p.display_name}
                        {visiblePlayerId(p.unique_player_id) && (
                          <span className="amigos-panel__friend-id">#{visiblePlayerId(p.unique_player_id)}</span>
                        )}
                      </span>
                      <span>
                        {p.friend_status ? (
                          <span className="amigos-panel__status">{statusBadge(p.friend_status)}</span>
                        ) : (
                          <button
                            type="button"
                            className="amigos-panel__mini-btn amigos-panel__mini-btn--accept"
                            onClick={() => sendRequest(p.player_profile_id)}
                          >
                            ➕ Agregar
                          </button>
                        )}
                      </span>
                    </div>
                  ))
                )}
                {!searchLoading && !hasSearched && (
                  <p className="amigos-panel__empty">Busca por nombre o ID para agregar jugadores a tu red.</p>
                )}
                {!searchLoading && hasSearched && searchResults.length === 0 && (
                  <p className="amigos-panel__empty">
                    No encontramos jugadores con “{searchQuery.trim()}”. Revisa el nombre, el correo o el ID.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Match Modal ──────────────────── */}
      {modal?.type === "createMatch" && (
        <div className="amigos-modal__overlay" onClick={() => setModal(null)}>
          <div className="amigos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="amigos-modal__header">
              <h3>🎾 Partido amistoso vs {modal.friend.friend.display_name}</h3>
              <button type="button" className="amigos-modal__close" onClick={() => setModal(null)}>
                ✕
              </button>
            </div>
            <div className="amigos-modal__body">
              <label className="amigos-modal__label">Fecha del partido</label>
              <input
                type="date"
                className="amigos-modal__input"
                value={matchDate}
                onChange={(e) => setMatchDate(e.target.value)}
              />
              <label className="amigos-modal__label">Notas (opcional)</label>
              <textarea
                className="amigos-modal__input amigos-modal__textarea"
                placeholder="¿Dónde jugaron? ¿Algún detalle?"
                value={matchNotes}
                onChange={(e) => setMatchNotes(e.target.value)}
                rows={2}
              />
              <div className="amigos-modal__actions">
                <button
                  type="button"
                  className="amigos-panel__action-btn"
                  onClick={() => setModal(null)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="amigos-panel__action-btn amigos-panel__action-btn--primary"
                  onClick={createMatch}
                  disabled={matchSubmitting}
                >
                  {matchSubmitting ? "Creando..." : "Crear partido"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Report Result Modal ─────────────────── */}
      {modal?.type === "reportResult" && (
        <div className="amigos-modal__overlay" onClick={() => setModal(null)}>
          <div className="amigos-modal amigos-modal--score" onClick={(e) => e.stopPropagation()}>
            <div className="amigos-modal__header amigos-modal__header--score">
              <div>
                <span className="amigos-modal__eyebrow">Partido amistoso</span>
                <h3>✏️ Reportar resultado</h3>
              </div>
              <button type="button" className="amigos-modal__close" onClick={() => setModal(null)}>
                ✕
              </button>
            </div>
            <div className="amigos-modal__body amigos-result-report">
              <div className="amigos-result-report__matchup" aria-label="Jugadores del amistoso">
                <span>{modal.match.player1.display_name}</span>
                <strong>vs</strong>
                <span>{modal.match.player2.display_name}</span>
              </div>

              <div className="amigos-result-scoreboard" aria-label="Captura de score amistoso por set">
                <div className="amigos-result-scoreboard__grid">
                  <div className="amigos-result-scoreboard__corner">Jugador</div>
                  <div className="amigos-result-scoreboard__winner-label">Ganador</div>
                  {FRIENDLY_SCORE_COLUMNS.map((column) => (
                    <div key={column} className="amigos-result-scoreboard__set-label">
                      {column}
                    </div>
                  ))}

                  {[modal.match.player1, modal.match.player2].map((player, index) => {
                    const rowKey = index === 0 ? "p1" : "p2";
                    const displayName = player.display_name ?? `Jugador #${player.player_profile_id}`;
                    return (
                      <div className="amigos-result-scoreboard__row" key={player.player_profile_id}>
                        <div className="amigos-result-scoreboard__player">
                          <span className="amigos-result-scoreboard__name">{displayName}</span>
                        </div>
                        <label className="amigos-result-scoreboard__winner-cell" aria-label={`Ganador ${displayName}`}>
                          <input
                            type="radio"
                            name={`friendly-winner-${modal.match.id}`}
                            checked={reportWinner === player.player_profile_id}
                            onChange={() => setReportWinner(player.player_profile_id)}
                          />
                          <span className="amigos-result-scoreboard__winner-indicator">✓</span>
                        </label>
                        {FRIENDLY_SCORE_COLUMNS.map((column) => (
                          <input
                            key={column}
                            className="amigos-result-scoreboard__score-input"
                            aria-label={`${column} ${displayName}`}
                            type="number"
                            min={0}
                            max={99}
                            inputMode="numeric"
                            value={reportScoreDraft[rowKey][column]}
                            onChange={(e) => updateReportScore(rowKey, column, e.target.value)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="amigos-result-report__hint">
                Captura los juegos por jugador. Hay espacio para hasta 5 sets y un TB; el sistema arma el score y los sets ganados automáticamente.
              </p>

              <div className="amigos-result-report__preview">
                <span>Score</span>
                <strong>{friendlyScoreTextFromDraft(reportScoreDraft) || "—"}</strong>
              </div>

              <div className="amigos-modal__actions amigos-modal__actions--sticky">
                <button
                  type="button"
                  className="amigos-panel__action-btn"
                  onClick={() => setModal(null)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="amigos-panel__action-btn amigos-panel__action-btn--primary"
                  onClick={reportResult}
                  disabled={reportSubmitting || reportWinner === null}
                >
                  {reportSubmitting ? "Guardando..." : "Guardar resultado"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

